"use client";

import type { Visit } from "@gettactic/api";
import { logError } from "@gettactic/helpers";
import { parseISO } from "date-fns";
import { formatBadgeDateTime } from "@/features/kiosk/lib/badge-date";
import {
  DEFAULT_BADGE_HEIGHT_MM,
  DEFAULT_BADGE_WIDTH_MM,
  renderBadgeToBase64
} from "@/features/kiosk/lib/badge-renderer";
import { printBadgeInBrowser } from "@/features/kiosk/lib/browser-badge-print";

export interface PrintVisitorBadgeOptions {
  /** Office time zone for the date line; the browser's zone is used when omitted. */
  timeZone?: string | null;
  /** Organization logo; the badge falls back to the Tactic lockup. */
  logoUrl?: string | null;
}

/**
 * Print a visitor's badge from the web app through the browser print dialog.
 * Uses the same badge renderer as the kiosk (default theme, colour ink,
 * 4×3in landscape) so both paths print the same design.
 */
export const printVisitorBadge = async (
  visitor: Visit,
  options: PrintVisitorBadgeOptions = {}
): Promise<void> => {
  const visitInstant = visitor.checked_in ?? visitor.expected;
  const imageBase64 = await renderBadgeToBase64({
    inkMode: "colour",
    visitorName: visitor.visitor?.name || "Unknown Visitor",
    visitorType: visitor.visitor_type?.name ?? null,
    hostName: visitor.host?.name ?? null,
    dateString: visitInstant
      ? formatBadgeDateTime(parseISO(visitInstant), options.timeZone)
      : "",
    photoUrl: visitor.guest_photo_url,
    logoUrl: options.logoUrl,
    targetWidthMm: DEFAULT_BADGE_WIDTH_MM,
    targetHeightMm: DEFAULT_BADGE_HEIGHT_MM,
    orientation: "landscape"
  });
  if (!imageBase64) {
    logError("Visitor badge could not be rendered", { visitId: visitor.id });
    return;
  }
  printBadgeInBrowser(imageBase64, {
    widthMm: DEFAULT_BADGE_WIDTH_MM,
    heightMm: DEFAULT_BADGE_HEIGHT_MM
  });
};
